/**
 * One tick's events -> the sounds to play (04 §5). At most one of each kind per tick: ten kills
 * landing together make one somewhat louder pop, panned to where they fell on average. Pure.
 */

import type { CapsuleKind, EnemyKind, SimEvent, SimEventType } from '../sim/types';

export type SoundId =
  | 'fire' | 'playerDied' | 'ready' | 'extraLife'
  | 'enemyHit' | 'kill' | 'captiveKilled' | 'dive' | 'bomb' | 'morph' | 'waveBonus' | 'morphBonus'
  | 'beamOn' | 'beamOff' | 'caught' | 'captured' | 'rescued' | 'docked'
  | 'badge' | 'challenge' | 'tally' | 'perfect' | 'gameOver'
  | 'capsule' | 'powerUp' | 'powerDown' | 'shieldBurst' | 'nova' | 'chain'
  | 'warning' | 'empressHit' | 'empressPhase' | 'empressBlast' | 'empressKilled'
  | 'lanceWarn' | 'lanceFire' | 'lanceEnd';

/**
 * Voice-steal ranks (voicePool.ts). The shot is at the bottom, so however fast it is fired it
 * never pushes anything else out. Pops and blips come next: what matters of them is the attack,
 * so in a crowd they cut each other's tails. The dive whistle and the beam's sweeps are above
 * those - a long note cut off is heard, and a kill must never do it - then the rare, big sounds.
 * Jingles and the fighter's death are at `TOP`, which nothing below can steal; menu blips rank
 * with them.
 */
export const TOP = 5;

export const PRIORITY: Readonly<Record<SoundId, number>> = {
  fire: 1, bomb: 1, capsule: 1,
  enemyHit: 2, kill: 2, morph: 2, waveBonus: 2, morphBonus: 2, badge: 2, powerDown: 2, chain: 2,
  empressHit: 2, empressBlast: 2, lanceEnd: 2,
  dive: 3, beamOn: 3, beamOff: 3,
  captiveKilled: 4, caught: 4, docked: 4, powerUp: 4, shieldBurst: 4, nova: 4, warning: 4, empressPhase: 4,
  empressKilled: 4, lanceWarn: 4, lanceFire: 4,
  playerDied: TOP, ready: TOP, extraLife: TOP, captured: TOP, rescued: TOP, challenge: TOP, tally: TOP,
  perfect: TOP, gameOver: TOP,
};

/** Sounds loud enough to pull the music (the Empress's theme) down under them. */
export const DUCKS: ReadonlySet<SoundId> = new Set<SoundId>([
  'playerDied', 'nova', 'empressPhase', 'empressKilled', 'lanceFire',
]);

/** Event types that deliberately make no sound: the next stage's badge blip follows a clear. */
export const SILENT: ReadonlySet<SimEventType> = new Set<SimEventType>(['stageClear']);

/** Enemy kinds as the kill and dive sounds index their pitches (a captive whistles like a Stinger). */
export const ENEMY_RANK: Readonly<Record<EnemyKind, number>> = {
  stinger: 0, moth: 1, morph: 2, monarch: 3, captive: 0,
};
/** Added to a kill's rank when the enemy was shot out of a dive: the fuller pop. */
export const DIVING = 4;
/** Capsule kinds as the power-up sounds index their figures. */
export const CAPSULE_KINDS: readonly CapsuleKind[] = ['rapid', 'spread', 'pierce', 'shield', 'chrono', 'nova', 'life'];

/** The screen's width in virtual pixels: an event at its middle is centred. */
const FIELD_WIDTH = 224;
/** Gentle: the cabinet had one speaker, so a sound never goes far to either side. */
export const MAX_PAN = 0.35;

export interface Hit {
  readonly sound: SoundId;
  /** How many events of this kind fell in the tick. */
  readonly count: number;
  /** -MAX_PAN (left) .. MAX_PAN (right). */
  readonly pan: number;
  /** The sound's parameter (`paramOf`): the largest of the tick's. */
  readonly n: number;
}

/** The sound an event makes, or null for one that is silent. */
export function soundOf(e: SimEvent): SoundId | null {
  switch (e.type) {
    case 'enemyKilled': return e.kind === 'captive' ? 'captiveKilled' : 'kill';
    case 'beam': return e.on ? 'beamOn' : 'beamOff';
    // The Empress's stage opens with her own `warning`.
    case 'stageStart': return e.kind === 'challenge' ? 'challenge' : e.kind === 'formation' ? 'badge' : null;
    case 'stageClear': return null;
    case 'tally': return e.perfect ? 'perfect' : 'tally';
    case 'lance': return e.phase === 'warn' ? 'lanceWarn' : e.phase === 'fire' ? 'lanceFire' : 'lanceEnd';
    case 'fire': case 'enemyHit': case 'dive': case 'bomb': case 'caught': case 'captured': case 'rescued':
    case 'docked': case 'playerDied': case 'ready': case 'waveBonus': case 'morph': case 'morphBonus':
    case 'extraLife': case 'gameOver': case 'capsule': case 'powerUp': case 'powerDown': case 'shieldBurst':
    case 'nova': case 'chain': case 'warning': case 'empressHit': case 'empressPhase': case 'empressBlast':
    case 'empressKilled':
      return e.type;
  }
}

/**
 * The number a sound is played with: 1 for a dual shot or a dual fighter's lost half, the kind's
 * rank for a kill (plus `DIVING`) or a dive, the hits for a tally, the level for a chain, the
 * capsule's place in `CAPSULE_KINDS`; otherwise 0.
 */
export function paramOf(e: SimEvent): number {
  switch (e.type) {
    case 'fire': return e.dual ? 1 : 0;
    case 'playerDied': return e.survivor ? 1 : 0;
    case 'enemyKilled': return ENEMY_RANK[e.kind] + (e.diving ? DIVING : 0);
    case 'dive': return ENEMY_RANK[e.kind];
    case 'tally': return e.hits;
    case 'chain': return e.level;
    case 'capsule': case 'powerUp': case 'powerDown': return CAPSULE_KINDS.indexOf(e.kind);
    default: return 0;
  }
}

export function panFor(x: number): number {
  const p = (x - FIELD_WIDTH / 2) / (FIELD_WIDTH / 2);
  return Math.max(-1, Math.min(1, p)) * MAX_PAN;
}

/** Gain for `count` of a kind at once: 1 for one, a little more for many, never double. */
export function loudness(count: number): number {
  if (count <= 1) return 1;
  return Math.min(1.6, 1 + 0.3 * Math.log2(count));
}

interface Group {
  count: number;
  sumX: number;
  nX: number;
  n: number;
  order: number;
}

/** The tick's sounds, most important first (so a full voice pool drops the trivial ones). */
export function collapse(events: readonly SimEvent[]): Hit[] {
  const groups = new Map<SoundId, Group>();
  for (const e of events) {
    const sound = soundOf(e);
    if (sound === null) continue;
    const n = paramOf(e);
    let g = groups.get(sound);
    if (!g) {
      g = { count: 0, sumX: 0, nX: 0, n, order: groups.size };
      groups.set(sound, g);
    }
    g.count++;
    // The biggest wins: a dual fighter's whole death over its half, a diving Monarch over a Stinger at home.
    g.n = sound === 'playerDied' ? Math.min(g.n, n) : Math.max(g.n, n);
    if ('x' in e) {
      g.sumX += e.x;
      g.nX++;
    }
  }
  // The beam goes out in the tick it delivers the fighter: the capture phrase speaks for both.
  if (groups.has('captured')) groups.delete('beamOff');
  const hits: (Hit & { order: number })[] = [];
  for (const [sound, g] of groups) {
    const pan = g.nX > 0 ? panFor(g.sumX / g.nX) : 0;
    hits.push({ sound, count: g.count, pan, n: g.n, order: g.order });
  }
  hits.sort((a, b) => PRIORITY[b.sound] - PRIORITY[a.sound] || a.order - b.order);
  return hits.map(({ sound, count, pan, n }) => ({ sound, count, pan, n }));
}

/**
 * Sounds too short to tell apart on back-to-back ticks (16.7 ms apart), and the seconds each
 * waits before it may sound again: 30 ms lets every other tick through. The thud of a hit on the
 * Empress waits longer, as a whole volley lands on her within a few ticks.
 */
export const QUICK: Readonly<Partial<Record<SoundId, number>>> = {
  fire: 0.03, bomb: 0.03, kill: 0.03, empressHit: 0.06,
};

/** Remembers when each quick sound last played, on whatever clock it is asked with. */
export class Retrigger {
  private readonly last = new Map<SoundId, number>();

  /** Whether `sound` may play at `now` (seconds); if so it is taken as played. */
  allow(sound: SoundId, now: number): boolean {
    const wait = QUICK[sound];
    if (wait === undefined) return true;
    const before = this.last.get(sound);
    if (before !== undefined && now >= before && now - before < wait) return false;
    this.last.set(sound, now);
    return true;
  }
}
